import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { PublicReviewAsset } from '../../../shared/types';
import { formatReviewTime } from './ReviewUi';

interface ReviewPlayerProps {
  asset: PublicReviewAsset | null;
  videoRef: RefObject<HTMLVideoElement>;
  mediaError: boolean;
  onTime: (seconds: number) => void;
  onMediaError: () => void;
}

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;
const controlButton = 'grid min-h-11 min-w-11 place-items-center rounded-full text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-[2.5px] focus-visible:outline-offset-3 focus-visible:outline-accent-on-dark';

export function nextMuteState(current: {
  muted: boolean;
  volume: number;
  lastNonZeroVolume: number;
}): { muted: boolean; volume: number } {
  if (current.volume === 0) {
    return {
      muted: false,
      volume: current.lastNonZeroVolume > 0 ? current.lastNonZeroVolume : 1,
    };
  }
  return { muted: !current.muted, volume: current.volume };
}

function PlayIcon({ paused }: { paused: boolean }) {
  return paused
    ? <svg className="h-5 w-5" viewBox="0 0 20 20" aria-hidden="true"><path d="m6 4 10 6-10 6z" fill="currentColor" /></svg>
    : <svg className="h-5 w-5" viewBox="0 0 20 20" aria-hidden="true"><path d="M5 4h3v12H5zm7 0h3v12h-3z" fill="currentColor" /></svg>;
}

function VolumeIcon({ muted }: { muted: boolean }) {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 8h3l4-3v10l-4-3H4z" fill="currentColor" />
      {muted
        ? <path d="m14 8 3 4m0-4-3 4" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
        : <path d="M14 7.2a4 4 0 0 1 0 5.6" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />}
    </svg>
  );
}

export default function ReviewPlayer({ asset, videoRef, mediaError, onTime, onMediaError }: ReviewPlayerProps) {
  const playerRef = useRef<HTMLElement>(null);
  const [paused, setPaused] = useState(true);
  const [duration, setDuration] = useState(asset?.duration_s ?? 0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const lastNonZeroVolumeRef = useRef(1);

  const updateTime = useCallback((seconds: number) => {
    setCurrentTime(seconds);
    onTime(seconds);
  }, [onTime]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play().catch(() => undefined);
    else video.pause();
  }, [videoRef]);

  const seekBy = useCallback((seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    const nextTime = Math.max(0, Math.min(Number.isFinite(video.duration) ? video.duration : Infinity, video.currentTime + seconds));
    video.currentTime = nextTime;
    updateTime(nextTime);
  }, [updateTime, videoRef]);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const next = nextMuteState({
      muted: video.muted,
      volume: video.volume,
      lastNonZeroVolume: lastNonZeroVolumeRef.current,
    });
    if (video.volume !== next.volume) video.volume = next.volume;
    if (video.muted !== next.muted) video.muted = next.muted;
  }, [videoRef]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }
    void playerRef.current?.requestFullscreen();
  }, []);

  useEffect(() => {
    setCurrentTime(0);
  }, [asset?.id]);

  useEffect(() => {
    function keyboard(event: KeyboardEvent) {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const key = event.key.toLocaleLowerCase('en-AU');
      if (event.key === ' ') {
        if (target?.closest('button, a, summary')) return;
        event.preventDefault();
        togglePlay();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        seekBy(event.key === 'ArrowLeft' ? -5 : 5);
      } else if (key === 'f') {
        event.preventDefault();
        toggleFullscreen();
      } else if (key === 'm') {
        event.preventDefault();
        toggleMute();
      }
    }
    document.addEventListener('keydown', keyboard);
    return () => document.removeEventListener('keydown', keyboard);
  }, [seekBy, toggleFullscreen, toggleMute, togglePlay]);

  useEffect(() => {
    function fullscreenChange() {
      setFullscreen(document.fullscreenElement === playerRef.current);
    }
    document.addEventListener('fullscreenchange', fullscreenChange);
    return () => document.removeEventListener('fullscreenchange', fullscreenChange);
  }, []);

  if (!asset) {
    return (
      <section className="rounded-card border border-dashed border-line bg-paper-200 p-7">
        <h2 className="text-2xl">No video is ready yet</h2>
        <p className="mt-2 text-sm text-muted">The transcript can appear once an interview has been added.</p>
      </section>
    );
  }

  const mediaAvailable = asset.media_available && asset.media_url !== null;
  const displayedTime = Math.min(currentTime, duration || currentTime);
  const effectiveMuted = muted || volume === 0;

  return (
    <section ref={playerRef} aria-labelledby="player-title" className="overflow-hidden rounded-[10px] border border-line bg-card shadow-card">
      {mediaAvailable ? (
        <div className="bg-dark">
          <div className="aspect-video">
            <video
              key={asset.id}
              ref={videoRef}
              className="h-full w-full bg-dark object-contain"
              src={asset.media_url ?? undefined}
              poster={asset.poster_url ?? undefined}
              playsInline
              preload="metadata"
              onClick={togglePlay}
              onPlay={() => setPaused(false)}
              onPause={() => setPaused(true)}
              onTimeUpdate={(event) => updateTime(event.currentTarget.currentTime)}
              onLoadedMetadata={(event) => {
                const video = event.currentTarget;
                setDuration(Number.isFinite(video.duration) ? video.duration : asset.duration_s ?? 0);
                setVolume(video.volume);
                if (video.volume > 0) lastNonZeroVolumeRef.current = video.volume;
                setMuted(video.muted);
                setPlaybackSpeed(video.playbackRate);
                updateTime(video.currentTime);
              }}
              onDurationChange={(event) => setDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : asset.duration_s ?? 0)}
              onVolumeChange={(event) => {
                const video = event.currentTarget;
                setVolume(video.volume);
                if (video.volume > 0) lastNonZeroVolumeRef.current = video.volume;
                setMuted(video.muted);
              }}
              onRateChange={(event) => setPlaybackSpeed(event.currentTarget.playbackRate)}
              onError={onMediaError}
            >
              Your browser cannot play this video. The transcript remains available below.
            </video>
          </div>
          <div className="player-chrome px-3 py-2 text-white md:px-4" aria-label="Video controls">
            <div className="flex items-center gap-3">
              <button type="button" className={controlButton} onClick={togglePlay} aria-label={paused ? 'Play video' : 'Pause video'} title={paused ? 'Play video' : 'Pause video'}>
                <PlayIcon paused={paused} />
              </button>
              <input
                className="player-range player-range-seek h-11 min-w-0 flex-1"
                style={{ '--range-progress': `${duration > 0 ? (displayedTime / duration) * 100 : 0}%` } as React.CSSProperties}
                type="range"
                min="0"
                max={Math.max(duration, 1)}
                step="0.01"
                value={Math.min(displayedTime, Math.max(duration, 1))}
                disabled={duration <= 0}
                onChange={(event) => {
                  const nextTime = Number(event.target.value);
                  if (videoRef.current) videoRef.current.currentTime = nextTime;
                  updateTime(nextTime);
                }}
                aria-label="Seek through video"
                aria-valuetext={`${formatReviewTime(displayedTime)} of ${formatReviewTime(duration)}`}
              />
              <output className="shrink-0 font-mono text-xs text-white/80" aria-label={`Current time ${formatReviewTime(displayedTime)} of ${formatReviewTime(duration)}`}>
                {formatReviewTime(displayedTime)} / {formatReviewTime(duration)}
              </output>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <button type="button" className={controlButton} onClick={toggleMute} aria-label={effectiveMuted ? 'Unmute video' : 'Mute video'} title={effectiveMuted ? 'Unmute video' : 'Mute video'}>
                <VolumeIcon muted={effectiveMuted} />
              </button>
              <label className="flex min-h-11 items-center gap-2 text-xs text-white/80">
                <span className="sr-only">Volume</span>
                <input
                  className="player-range w-24"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={(event) => {
                    const nextVolume = Number(event.target.value);
                    const video = videoRef.current;
                    if (!video) return;
                    if (nextVolume > 0) lastNonZeroVolumeRef.current = nextVolume;
                    video.volume = nextVolume;
                    if (nextVolume > 0) video.muted = false;
                  }}
                  aria-label="Volume"
                />
              </label>
              <label className="ml-auto flex min-h-11 items-center gap-2 font-mono text-xs text-white/80">
                <span className="uppercase tracking-[0.14em]">Speed</span>
                <select
                  className="player-speed"
                  value={playbackSpeed}
                  onChange={(event) => {
                    const nextSpeed = Number(event.target.value);
                    if (videoRef.current) videoRef.current.playbackRate = nextSpeed;
                    setPlaybackSpeed(nextSpeed);
                  }}
                  aria-label="Playback speed"
                >
                  {PLAYBACK_SPEEDS.map((speed) => <option key={speed} value={speed}>{speed}x</option>)}
                </select>
              </label>
              <button type="button" className={controlButton} onClick={toggleFullscreen} aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} title={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                <svg className="h-5 w-5" viewBox="0 0 20 20" aria-hidden="true">
                  {fullscreen
                    ? <path d="M8 4v4H4m8-4v4h4M8 16v-4H4m8 4v-4h4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
                    : <path d="M4 8V4h4m4 0h4v4m0 4v4h-4m-4 0H4v-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />}
                </svg>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid aspect-video place-items-center bg-elevated px-6 text-center">
          <div>
            <h3 className="text-[1.35rem] font-semibold">No video available for this interview.</h3>
            <p className="mt-1.5 text-sm text-muted">You can still read and mark the transcript.</p>
          </div>
        </div>
      )}
      <div className="p-4 md:p-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">Now reviewing</p>
        <h2 id="player-title" className="mt-1.5 text-2xl">{asset.title}</h2>
        {asset.speaker_label ? <p className="mt-1 text-sm text-muted">{asset.speaker_label}</p> : null}
      </div>
      {mediaAvailable && mediaError ? (
        <div className="border-t border-error/20 bg-error-tint px-5 py-4 text-sm text-error" role="alert">
          We could not load this video. You can still read and mark the transcript. Questions? Reply to Henry&apos;s email or call.
        </div>
      ) : null}
    </section>
  );
}
